import { useState, useRef, useEffect, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { AlertTriangle, AlertCircle, Loader2, Sparkles, Send, CornerDownLeft } from 'lucide-react'
import { Button, Input, Textarea, Modal, Select, Checkbox } from '@/components/ui'

const UNIT_OPTIONS = ['Std', 'Tag', 'Pauschal', 'Stk']

// ─── Exported Types ───────────────────────────────────────────────────────────

export interface ServiceCatalogCategory {
  id: string
  name: string
  description?: string
  sortOrder: number
  isActive: boolean
}

export interface SapTenantStatus {
  tenantId: string
  status: 'synced' | 'failed' | 'not_pushed'
  sapItemCode?: string
  pushedAt?: string
  errorMessage?: string
}

export interface ServiceCatalogItem {
  id: string
  tenantId?: string
  title: string
  description?: string
  categoryId?: string
  categoryName?: string
  priceCents: number
  timeHours: number
  unit: string
  sapItemCode?: string
  sapPushed: boolean
  sapPushedAt?: string
  sapTenantStatuses: SapTenantStatus[]
  confluencePageId?: string
  notes?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface AiGenerated {
  title?: string
  description?: string
  priceCents?: number
  timeHours?: number
  unit?: string
  notes?: string
  message?: string
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  parsed?: AiGenerated
}

// ─── Field Row ────────────────────────────────────────────────────────────────

function FieldRow({
  label,
  children,
  onApply,
}: {
  label: string
  children: ReactNode
  onApply: () => void
}) {
  return (
    <div className="flex items-center justify-between gap-2 py-1 border-b border-gray-100 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide leading-none mb-0.5">{label}</p>
        <div className="truncate">{children}</div>
      </div>
      <Button variant="ghost" size="xs" onClick={onApply} className="shrink-0 gap-1">
        <CornerDownLeft className="w-3 h-3" />
        Übernehmen
      </Button>
    </div>
  )
}

// ─── KI Chat Panel ───────────────────────────────────────────────────────────

export function KiChatPanel({
  onApply,
}: {
  onApply: (data: AiGenerated) => void
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, loading])

  async function send() {
    const trimmed = input.trim()
    if (!trimmed || loading) return

    setInput('')
    setError(null)

    const userMsg: ChatMessage = { role: 'user', content: trimmed }
    const nextMessages = [...messages, userMsg]
    setMessages(nextMessages)
    setLoading(true)

    try {
      // Build API message array — assistant turns send the raw JSON for context
      const apiMessages = nextMessages.map((m) => ({
        role: m.role,
        content:
          m.role === 'assistant' && m.parsed ? JSON.stringify(m.parsed) : m.content,
      }))

      const res = await apiClient.post('/service-catalog/ai-generate', { messages: apiMessages })
      const parsed = res.data as AiGenerated

      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: JSON.stringify(parsed),
        parsed,
      }
      setMessages((prev: ChatMessage[]) => [...prev, assistantMsg])
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string }
      setError(e.response?.data?.error ?? e.message ?? 'Unbekannter Fehler')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 h-full">
      <div className="flex items-center gap-2 text-sm font-medium text-brand-700">
        <Sparkles className="w-4 h-4" />
        KI-Assistent
      </div>

      {/* Chat history */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto space-y-3"
        style={{ maxHeight: '360px', minHeight: '120px' }}
      >
        {messages.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-6 px-2">
            Beschreibe die Leistung – die KI schlägt Titel, Beschreibung, Preis und Zeitaufwand vor. Du kannst die Vorschläge einzeln oder alle auf einmal übernehmen.
          </p>
        )}

        {messages.map((msg: ChatMessage, idx: number) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {msg.role === 'user' ? (
              <div className="bg-brand-600 text-white rounded-lg px-3 py-2 text-sm max-w-[85%] break-words">
                {msg.content}
              </div>
            ) : msg.parsed ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1.5 w-full">
                {msg.parsed.message && (
                  <p className="text-xs text-gray-600 italic pb-1">{msg.parsed.message}</p>
                )}

                {msg.parsed.title && (
                  <FieldRow
                    label="Titel"
                    onApply={() => onApply({ title: msg.parsed!.title })}
                  >
                    <span className="text-sm font-medium text-gray-900">{msg.parsed.title}</span>
                  </FieldRow>
                )}

                {msg.parsed.description && (
                  <FieldRow
                    label="Beschreibung"
                    onApply={() => onApply({ description: msg.parsed!.description })}
                  >
                    <span className="text-xs text-gray-700 line-clamp-2">{msg.parsed.description}</span>
                  </FieldRow>
                )}

                {msg.parsed.priceCents !== undefined && (
                  <FieldRow
                    label="Preis"
                    onApply={() => onApply({ priceCents: msg.parsed!.priceCents })}
                  >
                    <span className="text-sm font-semibold text-green-700">
                      {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
                        msg.parsed.priceCents / 100
                      )}
                    </span>
                  </FieldRow>
                )}

                {msg.parsed.timeHours !== undefined && (
                  <FieldRow
                    label="Zeitaufwand"
                    onApply={() =>
                      onApply({ timeHours: msg.parsed!.timeHours, unit: msg.parsed!.unit })
                    }
                  >
                    <span className="text-sm font-semibold text-gray-900">
                      {msg.parsed.timeHours} {msg.parsed.unit ?? 'Std'}
                    </span>
                  </FieldRow>
                )}

                {msg.parsed.notes && (
                  <FieldRow
                    label="Hinweise"
                    onApply={() => onApply({ notes: msg.parsed!.notes })}
                  >
                    <span className="text-xs text-gray-600">{msg.parsed.notes}</span>
                  </FieldRow>
                )}

                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => onApply(msg.parsed!)}
                  className="w-full mt-2"
                >
                  Alle übernehmen
                </Button>
              </div>
            ) : null}
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-lg px-3 py-2">
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2 flex items-start gap-1.5">
          <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {/* Input */}
      <div className="flex gap-2">
        <Input
          ref={inputRef}
          placeholder={
            messages.length === 0
              ? 'z.B. Windows-Laptop einrichten und ausliefern'
              : 'Verfeinern oder nachfragen…'
          }
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
          className="flex-1"
        />
        <Button
          variant="primary"
          size="sm"
          onClick={send}
          disabled={loading || !input.trim()}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  )
}

// ─── Item Form Modal ──────────────────────────────────────────────────────────

export function ItemFormModal({
  open,
  onOpenChange,
  item,
  categories,
  hasSyncTenants,
  defaultUnit,
  onSaved,
  onCreatedAndAdd,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  item: ServiceCatalogItem | null
  categories: ServiceCatalogCategory[]
  hasSyncTenants: boolean
  defaultUnit: string
  onSaved: () => void
  onCreatedAndAdd?: (item: ServiceCatalogItem) => void
}) {
  const queryClient = useQueryClient()
  const isNew = !item
  const addToQuoteRef = useRef(false)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [priceCents, setPriceCents] = useState(0)
  const [priceInput, setPriceInput] = useState('0,00')
  const [timeHours, setTimeHours] = useState(0)
  const [timeInput, setTimeInput] = useState('0')
  const [unit, setUnit] = useState(defaultUnit)
  const [notes, setNotes] = useState('')
  const [sapItemCode, setSapItemCode] = useState('')
  const [pushToSap, setPushToSap] = useState(false)
  const [showKiPanel, setShowKiPanel] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confluenceWarning, setConfluenceWarning] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setTitle(item?.title ?? '')
      setDescription(item?.description ?? '')
      setCategoryId(item?.categoryId ?? '')
      const pc = item?.priceCents ?? 0
      setPriceCents(pc)
      setPriceInput((pc / 100).toFixed(2).replace('.', ','))
      const th = item?.timeHours ?? 0
      setTimeHours(th)
      setTimeInput(String(th))
      setUnit(item?.unit ?? defaultUnit)
      setNotes(item?.notes ?? '')
      setSapItemCode(item?.sapItemCode ?? '')
      setPushToSap(false)
      setError(null)
      setConfluenceWarning(null)
      setShowKiPanel(false)
    }
  }, [open, item, defaultUnit])

  const saveMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      isNew
        ? apiClient.post('/service-catalog', data)
        : apiClient.patch(`/service-catalog/${item!.id}`, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['service-catalog-items'] })
      queryClient.invalidateQueries({ queryKey: ['service-catalog-count'] })
      const d = res.data as { sapError?: string; confluenceWarning?: string }
      if (d.sapError && !addToQuoteRef.current) {
        setError(`SAP-Push fehlgeschlagen: ${d.sapError}`)
        return
      }
      if (d.confluenceWarning) {
        setConfluenceWarning(d.confluenceWarning)
      }
      if (addToQuoteRef.current && onCreatedAndAdd) {
        onCreatedAndAdd(res.data)
        onOpenChange(false)
        return
      }
      if (!d.confluenceWarning) {
        onSaved()
        onOpenChange(false)
      }
    },
    onError: (err) => {
      const e = err as { response?: { data?: { error?: string } }; message?: string }
      setError(e.response?.data?.error ?? e.message ?? 'Fehler beim Speichern')
    },
  })

  function handlePriceBlur() {
    const val = parseFloat(priceInput.replace(',', '.')) || 0
    setPriceCents(Math.round(val * 100))
    setPriceInput(val.toFixed(2).replace('.', ','))
  }

  function handleTimeBlur() {
    const val = parseFloat(timeInput.replace(',', '.')) || 0
    setTimeHours(val)
    setTimeInput(String(val))
  }

  // Apply AI suggestions to form fields; panel stays open for continued chat
  function applyAiResult(data: AiGenerated) {
    if (data.title) setTitle(data.title)
    if (data.description) setDescription(data.description)
    if (data.priceCents !== undefined) {
      setPriceCents(data.priceCents)
      setPriceInput((data.priceCents / 100).toFixed(2).replace('.', ','))
    }
    if (data.timeHours !== undefined) {
      setTimeHours(data.timeHours)
      setTimeInput(String(data.timeHours))
    }
    if (data.unit) setUnit(data.unit)
    if (data.notes) setNotes(data.notes)
  }

  function doSave(forcePushToSapFalse?: boolean) {
    if (!title.trim()) { setError('Titel ist Pflichtfeld'); return }
    setError(null)
    saveMutation.mutate({
      title: title.trim(),
      description: description.trim() || undefined,
      categoryId: categoryId || undefined,
      priceCents,
      timeHours,
      unit,
      sapItemCode: sapItemCode.trim() || undefined,
      notes: notes.trim() || undefined,
      pushToSap: forcePushToSapFalse ? false : pushToSap,
    })
  }

  function handleSubmit() {
    addToQuoteRef.current = false
    doSave()
  }

  function handleSubmitAndAdd() {
    addToQuoteRef.current = true
    doSave(true)
  }

  const categoryOptions = [
    { value: '', label: 'Keine Kategorie' },
    ...categories.map((c) => ({ value: c.id, label: c.name })),
  ]

  const unitOptions = UNIT_OPTIONS.map((u) => ({ value: u, label: u }))

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isNew ? 'Neuer Servicekatalog-Eintrag' : 'Eintrag bearbeiten'}
      size="xl"
    >
      <div className="flex gap-6">
        {/* Main form */}
        <div className="flex-1 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Titel *</label>
            <div className="flex gap-2">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Leistungsbezeichnung"
                className="flex-1"
              />
              <Button
                variant={showKiPanel ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setShowKiPanel(!showKiPanel)}
                title="KI-Assistent ein-/ausblenden"
              >
                <Sparkles className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <Select
            label="Kategorie"
            value={categoryId}
            onValueChange={setCategoryId}
            options={categoryOptions}
            placeholder="Keine Kategorie"
          />

          <Textarea
            label="Beschreibung"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ausführliche Beschreibung der Leistung"
            rows={4}
          />

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Preis (€)</label>
              <Input
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                onBlur={handlePriceBlur}
                className="text-right"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Zeitaufwand</label>
              <Input
                value={timeInput}
                onChange={(e) => setTimeInput(e.target.value)}
                onBlur={handleTimeBlur}
                className="text-right"
              />
            </div>
            <Select
              label="Einheit"
              value={unit}
              onValueChange={setUnit}
              options={unitOptions}
            />
          </div>

          <Input
            label="Hinweise"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optionale Voraussetzungen oder Hinweise"
          />

          <Input
            label="SAP-Artikelnummer (optional)"
            value={sapItemCode}
            onChange={(e) => setSapItemCode(e.target.value)}
            placeholder="Wird automatisch vergeben wenn leer"
            className="font-mono"
          />

          {/* Push options */}
          {hasSyncTenants && (
            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <p className="text-xs font-medium text-gray-700 mb-2">Beim Speichern:</p>
              <Checkbox
                checked={pushToSap}
                onChange={(e) => setPushToSap(e.target.checked)}
                label="In SAP anlegen / aktualisieren (alle Sync-Mandanten)"
              />
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {confluenceWarning && (
            <div className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded p-2 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-yellow-600" />
              <div>
                <p className="font-medium">Artikel gespeichert – Confluence-Sync fehlgeschlagen</p>
                <p className="text-xs mt-0.5 text-yellow-700">{confluenceWarning}</p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              {confluenceWarning ? 'Schließen' : 'Abbrechen'}
            </Button>
            {isNew && onCreatedAndAdd && (
              <Button
                variant="accent"
                onClick={handleSubmitAndAdd}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending && addToQuoteRef.current
                  ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  : null}
                Anlegen und zur Position hinzufügen
              </Button>
            )}
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending && !addToQuoteRef.current
                ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                : null}
              {isNew ? 'Anlegen' : 'Speichern'}
            </Button>
          </div>
        </div>

        {/* KI chat panel */}
        {showKiPanel && (
          <div className="w-80 shrink-0 border-l border-gray-200 pl-6 flex flex-col">
            <KiChatPanel onApply={applyAiResult} />
          </div>
        )}
      </div>
    </Modal>
  )
}
